import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Pagination,
  Row,
  Typography,
  Tooltip,
  Spin
} from "antd";
import {
  CheckCircleFilled,
  ClockCircleFilled,
  SyncOutlined,
  FileDoneOutlined,
  StopOutlined,
  MinusCircleFilled,
  FolderFilled,
  CloseCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import useResponsiveSettings from "../../hooks/useResponsiveSettings";
import eventService from "../../services/eventsService";
import lookupService from "../../services/lookupService";
import { recordError } from "src/utils/logService";
import { EventCard, isWithinEventCaptureWindow } from "./EventCard";

import {
  EVENT_ACTIONS,
  ACTION_CONFIRMATION_CONFIG,
} from "./eventActionConfig";
import { EVENT_STATUS_STORE_NAME } from "../../constants";
import { confirmAction } from "../confirmAction";
import { Notify } from "../../services";
import { openNotification } from "../custom";

import "./styles/events-app.css";

const { Text } = Typography;

/** Status styles: bg + icon color + label color */
const STATUS_STYLES = {
  "Submitted": { bg: "#BCE2F2", color: "#0A74C5", icon: <FileDoneOutlined style={{ color: "#0A74C5" }} /> },
  "In Progress": { bg: "#FAE7BE", color: "#9A7400", icon: <SyncOutlined style={{ color: "#9A7400" }} /> },
  "In Revision": { bg: "#EFEFEF", color: "#555", icon: <ClockCircleFilled style={{ color: "#555" }} /> },
  "Approved": { bg: "#C7ECC9", color: "#2DA751", icon: <CheckCircleFilled style={{ color: "#1A7F37" }} /> },
  "Rejected": { bg: "#FCE2E2", color: "#C62828", icon: <CloseCircleFilled style={{ color: "#C62828" }} /> },
  "Cancelled": { bg: "#EEEEEE", color: "#777", icon: <StopOutlined style={{ color: "#777" }} /> },
  "Abandoned": { bg: "#EEEEEE", color: "#555", icon: <MinusCircleFilled style={{ color: "#555" }} /> },
  "Concluded": { bg: "#E8D6FF", color: "#5A3DB9", icon: <ClockCircleFilled style={{ color: "#5A3DB9" }} /> },
  "Archived": { bg: "#EEE8E8", color: "#666", icon: <FolderFilled style={{ color: "#666" }} /> },
};


const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const isOffline = () => !navigator.onLine;

export const ListingEvents = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const isOnline = navigator.onLine;

  const { pageSize } = useResponsiveSettings({ xxlPageSize: 8 });

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  /* ================================
     LOAD LOOKUPS + EVENTS
     ================================ */

  useEffect(() => {
    lookupService
      .getCahedLookup(EVENT_STATUS_STORE_NAME)
      .then((res) => res?.status === 200 && setStatuses(res.data))
      .catch(recordError);

    loadEvents();
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await eventService.getEvents({ category: "all" });
      if (res?.status === 200) {
        setEvents(
          res.data.map(ev => ({
            ...ev,
            title: ev.name,
            subtitle: ev.type,
            date: formatDate(ev.startDate),
            statusText: ev.status?.value,
            location: ev.venue?.name,
            participants: ev.totalParticipants,
          }))
        );
      }
    } catch (err) {
      recordError(err, 'ERROR', 'events');
    } finally {
      setLoading(false);
    }

  }, []);

  useEffect(() => {
    const handleSyncComplete = () => {
      console.log("[Events] Sync complete → reload listing");
      loadEvents();
    };

    window.addEventListener("offline-queue-sync-complete", handleSyncComplete);

    return () => {
      window.removeEventListener("offline-queue-sync-complete", handleSyncComplete);
    };
  }, [loadEvents]);

  /* ================================
     FILTERING + PAGINATION
     ================================ */

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      const matchesSearch = e.title
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesStatus = statusFilter ? e.statusText === statusFilter : true;

      return matchesSearch && matchesStatus;
    });
  }, [events, search, statusFilter]);

  const eventsWithSortKeys = useMemo(() => {
    const now = Date.now();

    return filteredEvents.map(event => {

      const start = Date.parse(event.startDate);
      const hasValidStart = Number.isFinite(start);

      // Hard invariant: active events always win (0 = highest priority)
      const phase = isWithinEventCaptureWindow(event, now) ? 0 : 1;

      // Soft relevance: time distance from now (lower = more relevant)
      // Only matters within the same phase
      const timeDistance = hasValidStart ? Math.abs(start - now): Number.POSITIVE_INFINITY;

      return {
        event,

        // Stable comparable keys
        _phase: phase,
        _timeDistance: timeDistance,
        _startTime: hasValidStart ? start : -Infinity,
      };
    });
  }, [filteredEvents]);

  // ONLY active + approved events get priority
  const sortedEvents = useMemo(() => {
    return [...eventsWithSortKeys]
      .sort((a, b) => {
        // Hard invariant first - capturing
        if (a._phase !== b._phase) {
          return a._phase - b._phase;
        }

        // Time gradient within phase
        if (a._timeDistance !== b._timeDistance) {
          return a._timeDistance - b._timeDistance;
        }

        // Stable tie‑breaker
        return b._startTime - a._startTime;
      })
      .map(x => x.event);
  }, [eventsWithSortKeys]);

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedEvents.slice(start, start + pageSize);
  }, [sortedEvents, currentPage, pageSize]);

  const eventsTitle = useMemo(() => {
    const trimmedSearch = search.trim();
    const hasSearch = trimmedSearch.length > 0;
    const hasStatus = typeof statusFilter === "string" && statusFilter.length > 0;

    // 1. No filters at all
    if (!hasSearch && !hasStatus) {
      return "All Events";
    }

    // 2. Status only
    if (hasStatus && !hasSearch) {
      return `${statusFilter} Events`;
    }

    // 3. Search only
    if (!hasStatus && hasSearch) {
      return `Results for "${search}"`;
    }

    // 4. Status + search
    return `${statusFilter} Events matching "${search}"`;
  }, [statusFilter, search]);

  /* ================================
     ACTION HANDLING
     ================================ */

  const performEventAction = async (action, event, payload = {}) => {
    switch (action) {
      case EVENT_ACTIONS.CAPTURE:
        navigate(
          `/events/capture/${event.id}/attendees`,
          {
            state: {
              eventName: event.title,
            },
          }
        );
        break;

      case EVENT_ACTIONS.ADD_CAPTURER:
        navigate(`/events/data-capturers/${event.id}`, {
          state: {
            eventName: event.title,
            eventStatus: event.statusText,
          },
        });
        break;

      case EVENT_ACTIONS.VIEW:
        navigate(`/events/view/${event.id}`, {
          state: {
            eventName: event.title,
            eventStatus: event.statusText,
          },
        });
        break;

      case EVENT_ACTIONS.EDIT:
        navigate(`/events/edit/${event.id}`, {
          state: {
            eventName: event.title,
            eventStatus: event.statusText,
          },
        });
        break;

      case EVENT_ACTIONS.CANCEL:
        await eventService.updateEventStatus({
          action: EVENT_ACTIONS.CANCEL,
          notes: payload?.reason || "",
          eventId: event.id,
        })
          .then((res) => res?.status === 200 &&
            openNotification(
              "Event Cancellation",
              `Successfully cancelled ${event?.name || 'event'}`,
              Notify.SUCCESS,
            )
          )
          .catch((err) =>{
            openNotification(
              "Event Cancellation",
              "Something went wrong. Please try again.",
              Notify.ERROR,
            );
            recordError(err, 'ERROR', 'events');
          });
        loadEvents(); // refresh list
        break;

      case EVENT_ACTIONS.CONCLUDE:
        await eventService.updateEventStatus({
          action: EVENT_ACTIONS.CONCLUDE,
          notes: payload?.reason || "Done",
          eventId: event.id,
        })
          .then((res) => res?.status === 200 &&
            openNotification(
              "Event Conclusion",
              `Successfully concluded ${event?.name || 'event'}`,
              Notify.SUCCESS,
            )
          )
          .catch((err) => {
            openNotification(
              "Event Conclusion",
              "Something went wrong. Please try again.",
              Notify.ERROR,
            );
            recordError(err, 'ERROR', 'events');
          });
        loadEvents(); // refresh list
        break;

      default:
        break;
    }
  };

  const resolveConfirmConfig = (config, context = {}) => {
    return {
      title:
        typeof config.title === "function"
          ? config.title(context.event)
          : config.title,

      description: config.description,

      requireReason: config.requiresReason,
      minReasonLength: config.requiresReason ? config.minReasonLength ?? 10 : 0,

      danger: config.danger,
    };
  };

/**
 * Central menu action handler for Event listings
 *
 * Responsibilities:
 * - Offline protection for backend actions
 * - Trigger confirmations (Modal / Popconfirm via config)
 * - Perform side-effects
 * - Show success / error feedback
 */
  const handleMenuAction = async (action, event) => {
    const config = ACTION_CONFIRMATION_CONFIG[action];

    // Block backend actions offline
    if (config?.requiresBackend && isOffline()) {
      message.warning(
        "This action is unavailable while offline."
      );
      return;
    }

    const perform = async (payload = {}) => {
      try {
        await performEventAction(action, event, payload);
        // message.success("Action completed successfully.");
      } catch (err) {
        openNotification(
          "Event Actions",
          "Something went wrong. Please try again.",
          Notify.ERROR,
        );
        recordError(err);
      }
    };

    // Modal confirmation
    if (config?.mode === "modal") {
      const resolved = resolveConfirmConfig(config, { event });

      confirmAction({
        ...resolved,
        onOk: ({ reason }) => {
          return perform({ reason });
        },
      });
      return;
    }

    // Popconfirm handled declaratively in menu
    perform();
  };

  /* ================================
     RENDER
     ================================ */

  return (
    <div className="events-app">
      <Row justify="center">
      <Col xs={24} sm={24} md={24} lg={24} xl={18}>
      {/* CREATE EVENT */}
      <Card variant="borderless" className="create-event-card">
        <div className="create-event-card__content">
          <Text className="create-event-card__text">
            Create your own event
          </Text>
            <Tooltip
              title={isOnline ? "" : "Cannot create events while offline"}
              trigger={["hover", "click"]}
            >
              <Button
                type="primary"
                className="events-btn"
                disabled={!isOnline}
                onClick={() => navigate("/events/add")}
              >
                create
              </Button>
            </Tooltip>
        </div>
      </Card>

      {/* FILTERS */}
      <Card className="events-app__filters">
        <Input
          className="events-app__search"
          prefix={<SearchOutlined />}
          placeholder="search for an event"
          allowClear
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />

        <h4 className="events-app__filter-title">
          Filter by status
        </h4>

        {/* STATUS FILTERS (HORIZONTAL SCROLL) */}
        <div className="events-app__statuses-scroll">
          <div className="events-app__statuses">
            {statuses.map((item) => {
              const isActive = statusFilter === item.value;
              const style = STATUS_STYLES[item.value];
              if (!style) return null;

              return (
                <Tooltip
                  key={`status-tooltip-${item.key}`}
                  title={
                    isActive
                      ? "Click again to clear filter"
                      : `Click to filter by ${item.value}`
                  }
                  trigger={["hover", "click"]}
                >
                  <button
                    type="button"
                    data-testid={`status-pill-${String(item.value).toLowerCase().replace(/\s+/g, "-")}`}
                    className={`status-pill ${
                      isActive
                        ? "status-pill--active"
                        : ""
                    }`}
                    style={{ color: style.color }}
                    aria-pressed={isActive}
                    onClick={() => {
                      setCurrentPage(1);
                      setStatusFilter(
                        isActive ? null : item.value
                      );
                    }}
                  >
                    <span
                      className="status-pill__icon"
                      style={{ background: style.bg }}
                      aria-hidden="true"
                    >
                      {style.icon}
                    </span>

                    <span className="status-pill__label">
                      {item.value}
                    </span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>

        <h4 className="events-app__subtitle">
          {eventsTitle}
        </h4>

        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 0",
            }}
          >
            <Spin size="large" />
            <Text style={{ marginTop: 12, color: "#555" }}>
              Loading event, please wait...
            </Text>
          </div>
        )}

        {!loading && filteredEvents.length === 0 && (
          <div className="events-app__empty">
            <b>No events found</b>
          </div>
        )}

        {!loading && filteredEvents.length > 0 && (
          <Row gutter={[16, 16]}>
            {paginatedEvents.map((ev) => (
              <Col xs={24} md={12} xl={8} xxl={6} key={`event-${ev.id}`}>
                <EventCard
                  event={ev}
                  statusStyle={STATUS_STYLES[ev.statusText]}
                  onPrimaryAction={(event) =>
                    performEventAction(EVENT_ACTIONS.CAPTURE, event)
                  }
                  onMenuAction={handleMenuAction}
                />
              </Col>
            ))}
          </Row>
        )}
      </Card>

      {/* PAGINATION */}
      <div className="events-app-pagination">
        <Pagination
          current={currentPage}
          onChange={setCurrentPage}
          total={filteredEvents.length}
          pageSize={pageSize}
          showTotal={(total, range) =>
            `Showing ${range[0]}-${range[1]} of ${total} results`
          }
        />
      </div>
      
      </Col>
      </Row>
    </div>
  );
};
